import { memo, useMemo, useState } from "react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import {
  AlertTriangle, ChevronLeft, ChevronRight, FileText, GitBranch, GitCompare,
  GitFork, MessageSquare, Scale, Sparkles, StickyNote,
} from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import type { AttachmentKind, GraphEdge, GraphNode, ID, NodeVariant, VariantIntent } from "@/types";
import { nodeMeta, provenanceMeta } from "@/lib/vocabulary";
import { wordDiff } from "@/lib/text-diff";
import { cn } from "@/lib/cn";

// react flow expects a typed node; we carry the domain node in `data.node`
export interface AttachedModifier {
  node: GraphNode;
  edge: GraphEdge;
}

export interface StrainNodeData {
  node: GraphNode;
  isSelected: boolean;
  isHighlighted: boolean;
  isDimmed: boolean;
  visualRole?: "thesis" | "critique" | "bridge" | "evidence" | "warrant" | "criterion" | "default";
  hiddenCritiqueCount?: number;
  onRevealCritiques?: (nodeId?: ID, edgeId?: ID) => void;
  // modifiers docked to this node (exceptions/caveats); revealed on focus
  attachedModifiers?: AttachedModifier[];
  selectedModifierId?: ID | null;
  onSelectModifier?: (nodeId: ID) => void;
  // variant interactions
  onPreferVariant?: (nodeId: ID, variantId: ID) => void;
  preferredVariantIds?: ID[];
  [key: string]: unknown;
}

export const ATTACHMENT_META: Record<AttachmentKind, { label: string; icon: ReactNode; color: string; description: string }> = {
  exception: {
    label: "Exception",
    icon: <AlertTriangle size={10} />,
    color: "var(--color-qualify)",
    description: "Carves a population or context out of the claim without weakening the main chain.",
  },
  caveat: {
    label: "Caveat",
    icon: <StickyNote size={10} />,
    color: "var(--color-ink-muted)",
    description: "A footnote-grade limitation: important to know, not load-bearing.",
  },
};

export const INTENT_META: Record<VariantIntent, { label: string; color: string }> = {
  rewording: { label: "rewording", color: "var(--color-ink-muted)" },
  steelman: { label: "steelman", color: "var(--color-primary)" },
  narrow: { label: "narrow", color: "var(--color-qualify)" },
  broaden: { label: "broaden", color: "var(--color-define)" },
  reframe: { label: "reframe", color: "var(--color-reframe)" },
};

function StrainNodeComponent({ data }: NodeProps) {
  const d = data as StrainNodeData;
  const node = d.node;
  const meta = nodeMeta(node.type);
  const prov = provenanceMeta(node.provenance);
  const isInferred = node.provenance === "ai_inferred";
  const isCrux = node.badges?.includes("crux") || !!node.crux;
  const isThesis = node.badges?.includes("thesis");
  const visualRole = d.visualRole ?? (isThesis ? "thesis" : "default");
  const isCritique = visualRole === "critique";
  const isBridge = visualRole === "bridge";
  const isEvidence = visualRole === "evidence";
  const isWarrant = visualRole === "warrant";
  const isCriterion = visualRole === "criterion";
  const Icon = meta.icon;
  const stats = node.stats;
  const isFocused = d.isSelected || d.isHighlighted;

  // ---- version rail state (view-only: the canonical text stays canonical) ----
  const variants = node.variants ?? [];
  const slotEntries = useMemo(() => ([
    { id: "__canonical", intent: null as VariantIntent | null, author: node.author ?? null, text: node.text, note: null as string | null, prefers: -1 },
    ...variants.map((v) => ({ id: v.id, intent: v.intent, author: v.author ?? null, text: v.text, note: v.note ?? null, prefers: v.prefers })),
  ]), [node.author, node.text, variants]);
  const [slotIndex, setSlotIndex] = useState(0);
  const [showDiff, setShowDiff] = useState(false);
  const activeIndex = Math.min(slotIndex, slotEntries.length - 1);
  const active = slotEntries[activeIndex];
  const viewingVariant = activeIndex > 0;
  const displayText = active.text;

  const modifiers = d.attachedModifiers ?? [];
  const exceptionCount = modifiers.filter((m) => m.node.attachment?.kind === "exception").length;
  const caveatCount = modifiers.length - exceptionCount;
  const showModifierCards = isFocused && modifiers.length > 0;
  const showRail = d.isSelected && variants.length > 0;

  const roleColor = isCritique
    ? "var(--color-rebut)"
    : isBridge
    ? "var(--color-primary-bright)"
    : meta.color;
  const nodeStyle = {
    borderColor: isThesis
      ? "var(--color-primary)"
      : `color-mix(in srgb, ${roleColor} ${d.isSelected || d.isHighlighted ? 70 : isCritique ? 62 : 44}%, var(--color-border) 56%)`,
    background: isCritique
      ? "color-mix(in srgb, var(--color-rebut) 10%, var(--color-surface) 90%)"
      : isWarrant
      ? "color-mix(in srgb, var(--color-inferred) 8%, var(--color-surface) 92%)"
      : isCriterion
      ? "color-mix(in srgb, var(--color-define) 7%, var(--color-surface) 93%)"
      : isBridge
      ? "color-mix(in srgb, var(--color-primary) 9%, var(--color-surface) 91%)"
      : `linear-gradient(90deg, color-mix(in srgb, ${meta.color} 9%, var(--color-surface) 91%) 0%, var(--color-surface) 64%)`,
  } satisfies CSSProperties;
  const widthClass = isThesis
    ? "w-[300px] max-w-[300px]"
    : isEvidence || isCriterion || isWarrant
    ? "w-[260px] max-w-[260px]"
    : "w-[280px] max-w-[280px]";

  return (
    <div
      style={nodeStyle}
      className={cn(
        "relative overflow-hidden border transition-all duration-150",
        widthClass,
        isCritique ? "rounded-sm" : isEvidence ? "rounded-md" : isCriterion ? "rounded-lg" : isBridge ? "rounded-lg" : "rounded-xl",
        isWarrant && "border-dashed",
        isThesis && "shadow-glow-primary",
        d.isSelected && "ring-2 ring-[var(--color-primary)] ring-offset-2 ring-offset-[var(--color-base)]",
        d.isHighlighted && "shadow-glow-primary border-[var(--color-primary)]",
        d.isDimmed && "opacity-35",
      )}
    >
      {!isCritique && !isWarrant && (
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-8 opacity-55"
          style={{
            background: `linear-gradient(90deg, color-mix(in srgb, ${roleColor} 16%, transparent), transparent 78%)`,
          }}
        />
      )}
      {isEvidence && (
        <span className="pointer-events-none absolute right-0 top-0 h-5 w-5 border-b border-l border-[var(--color-evidence)]/45 bg-[var(--color-surface-2)]" />
      )}
      {isCritique && (
        <span className="pointer-events-none absolute right-2 top-2 h-3 w-3 border-r-2 border-t-2 border-[var(--color-rebut)]" />
      )}
      {isBridge && (
        <span className="pointer-events-none absolute inset-x-3 top-0 h-px bg-[var(--color-primary)]/50" />
      )}
      <Handle
        type="target"
        position={Position.Left}
        className="!w-2 !h-2 !bg-[var(--color-border-bright)] !border-[var(--color-surface)]"
      />

      <div className="relative px-3 py-2.5 pl-3.5">
        {/* header row: one metadata line — type, provenance, core status marks */}
        <div className="flex items-center gap-1.5 mb-1.5">
          <span
            className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border"
            style={{
              color: roleColor,
              background: `color-mix(in srgb, ${roleColor} 16%, var(--color-surface) 84%)`,
              borderColor: `color-mix(in srgb, ${roleColor} 42%, var(--color-border) 58%)`,
            }}
          >
            <Icon size={10} /> {meta.short}
          </span>
          {isCritique && (
            <span className="inline-flex items-center gap-0.5 font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-[var(--color-rebut)]/35 text-[var(--color-rebut)] bg-[color-mix(in_srgb,var(--color-rebut)_10%,var(--color-surface)_90%)]">
              critique
            </span>
          )}
          {isInferred && (
            <span
              className="inline-flex items-center gap-0.5 font-mono text-[9px] uppercase tracking-wider px-1 py-0.5 rounded text-[var(--color-inferred)] animate-inferred"
              title={prov.note}
            >
              <Sparkles size={9} /> inferred
            </span>
          )}
          {node.status === "needs_evidence" && (
            <span
              className="h-1.5 w-1.5 rounded-full bg-[var(--color-secondary)]"
              title="needs evidence"
              aria-label="needs evidence"
            />
          )}
          {isCrux && !isThesis && (
            <span className="ml-auto inline-flex items-center gap-0.5 font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded text-[var(--color-secondary)] bg-[rgba(251,191,36,0.14)] border border-[rgba(251,191,36,0.3)] animate-crux-pulse">
              crux
            </span>
          )}
          {isThesis && (
            <span className="ml-auto font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded text-[var(--color-primary)] bg-[rgba(94,234,212,0.12)]">
              thesis
            </span>
          )}
          {!isCritique && (d.hiddenCritiqueCount ?? 0) > 0 && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                d.onRevealCritiques?.(node.id);
              }}
              className={cn(
                "inline-flex h-5 cursor-pointer items-center gap-0.5 rounded-full border border-[var(--color-rebut)] bg-[var(--color-surface)] px-1.5 transition-colors hover:bg-[color-mix(in_srgb,var(--color-rebut)_10%,var(--color-surface)_90%)]",
                isThesis || isCrux ? "ml-1" : "ml-auto",
              )}
              title={`${d.hiddenCritiqueCount} hidden critique${d.hiddenCritiqueCount === 1 ? "" : "s"}`}
            >
              {Array.from({ length: Math.min(d.hiddenCritiqueCount ?? 0, 4) }).map((_, index) => (
                <span key={index} className="h-1.5 w-1.5 rounded-full bg-[var(--color-rebut)]" />
              ))}
            </button>
          )}
        </div>

        {/* version rail — competing wordings of this slot, focused only */}
        {showRail && (
          <div className="mb-2 flex items-center gap-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] px-1.5 py-1 nodrag">
            <RailButton
              disabled={activeIndex === 0}
              onClick={() => { setSlotIndex(Math.max(0, activeIndex - 1)); }}
              icon={<ChevronLeft size={12} />}
              label="Previous wording"
            />
            <div className="flex items-center gap-1">
              {slotEntries.map((entry, index) => (
                <button
                  key={entry.id}
                  type="button"
                  onClick={(event) => { event.stopPropagation(); setSlotIndex(index); }}
                  title={index === 0 ? "canonical wording" : `${INTENT_META[entry.intent!].label}${entry.author ? ` · @${entry.author}` : ""}`}
                  className={cn(
                    "h-1.5 cursor-pointer rounded-full transition-all",
                    index === activeIndex ? "w-4" : "w-1.5 opacity-40 hover:opacity-80",
                  )}
                  style={{ background: index === 0 ? "var(--color-ink-soft)" : INTENT_META[entry.intent!].color }}
                />
              ))}
            </div>
            <RailButton
              disabled={activeIndex === slotEntries.length - 1}
              onClick={() => { setSlotIndex(Math.min(slotEntries.length - 1, activeIndex + 1)); }}
              icon={<ChevronRight size={12} />}
              label="Next wording"
            />
            <span
              className="inline-flex min-w-0 items-center gap-1 truncate font-mono text-[9px] uppercase tracking-wider"
              style={{ color: viewingVariant ? INTENT_META[active.intent!].color : "var(--color-ink-muted)" }}
            >
              {viewingVariant ? INTENT_META[active.intent!].label : "canonical"}
              {active.author && <span className="truncate text-[var(--color-ink-faint)]">@{active.author}</span>}
            </span>
            <button
              type="button"
              onClick={(event) => { event.stopPropagation(); setShowDiff((v) => !v); }}
              title="Compare with the canonical wording"
              disabled={!viewingVariant}
              className={cn(
                "ml-auto inline-flex h-5 items-center gap-1 rounded border px-1.5 font-mono text-[9px] uppercase tracking-wider transition-colors",
                showDiff && viewingVariant
                  ? "border-[var(--color-primary)] text-[var(--color-primary)]"
                  : "border-[var(--color-border)] text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]",
                !viewingVariant && "opacity-30",
              )}
            >
              <GitCompare size={9} /> diff
            </button>
          </div>
        )}

        {/* claim text — the idea itself */}
        <div
          key={active.id}
          className={cn(
            "leading-[1.42] animate-variant-swap",
            isThesis ? "font-serif text-[15px] font-medium text-[var(--color-ink)]" : "text-[13px] text-[var(--color-ink-soft)]",
            isCritique && "font-medium text-[var(--color-ink)]",
          )}
        >
          {showDiff && viewingVariant ? <DiffText from={node.text} to={displayText} /> : displayText}
        </div>

        {/* variant footer: prefer count + toggle, focused only */}
        {showRail && viewingVariant && (
          <div className="mt-1.5 flex items-center gap-2 nodrag">
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                d.onPreferVariant?.(node.id, active.id);
              }}
              className={cn(
                "inline-flex h-5 items-center gap-1 rounded-full border px-1.5 font-mono text-[9px] uppercase tracking-wider transition-colors",
                d.preferredVariantIds?.includes(active.id)
                  ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                  : "border-[var(--color-border)] text-[var(--color-ink-muted)] hover:border-[var(--color-primary)] hover:text-[var(--color-ink)]",
              )}
              title="Prefer this wording"
            >
              <Scale size={9} /> {active.prefers} prefer
            </button>
            {active.note && (
              <span className="truncate text-[10px] text-[var(--color-ink-faint)]">{active.note}</span>
            )}
          </div>
        )}

        {/* attachment tray — always-visible scent that more exists here */}
        {(variants.length > 0 || modifiers.length > 0) && (
          <div className="mt-2 flex items-center gap-1.5 border-t border-[var(--color-border)]/60 pt-2">
            {variants.length > 0 && (
              <TrayChip icon={<GitBranch size={9} />} color="var(--color-primary)" label={`${variants.length + 1} wordings`} />
            )}
            {exceptionCount > 0 && (
              <TrayChip icon={<AlertTriangle size={9} />} color="var(--color-qualify)" label={`${exceptionCount} exception${exceptionCount === 1 ? "" : "s"}`} />
            )}
            {caveatCount > 0 && (
              <TrayChip icon={<StickyNote size={9} />} color="var(--color-ink-muted)" label={`${caveatCount} caveat${caveatCount === 1 ? "" : "s"}`} />
            )}
          </div>
        )}

        {/* docked modifiers — attached, subordinate, revealed on focus */}
        {showModifierCards && (
          <div className="mt-2 space-y-1.5 nodrag">
            {modifiers.map((modifier, index) => {
              const kind = modifier.node.attachment?.kind ?? "caveat";
              const kindMeta = ATTACHMENT_META[kind];
              const selected = d.selectedModifierId === modifier.node.id;
              return (
                <button
                  key={modifier.node.id}
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    d.onSelectModifier?.(modifier.node.id);
                  }}
                  style={{
                    animationDelay: `${index * 50}ms`,
                    borderColor: `color-mix(in srgb, ${kindMeta.color} 32%, var(--color-border) 68%)`,
                    borderLeft: `2px solid ${kindMeta.color}`,
                  }}
                  className={cn(
                    "block w-full animate-modifier-in rounded-md border bg-[var(--color-surface-2)] p-2 text-left transition-colors hover:bg-[var(--color-surface-3)]",
                    selected && "ring-1 ring-[var(--color-primary)]",
                  )}
                  title={kindMeta.description}
                >
                  <div className="mb-0.5 flex items-center gap-1 font-mono text-[9px] uppercase tracking-wider" style={{ color: kindMeta.color }}>
                    {kindMeta.icon}
                    <span>{kindMeta.label}</span>
                    {modifier.node.author && <span className="ml-auto text-[var(--color-ink-faint)]">@{modifier.node.author}</span>}
                  </div>
                  <div className="text-[11px] leading-[1.4] text-[var(--color-ink-soft)]">{modifier.node.text}</div>
                </button>
              );
            })}
          </div>
        )}

        {/* activity + stance — focused only, and never invented */}
        {stats && isFocused && (
          <div className="flex items-center gap-3 mt-2 pt-2 border-t border-[var(--color-border)]/60">
            {stats.comments > 0 && (
              <Indicator icon={<MessageSquare size={11} />} value={stats.comments} tone="muted" />
            )}
            {stats.evidence > 0 && (
              <Indicator icon={<FileText size={11} />} value={stats.evidence} tone="evidence" />
            )}
            {stats.forks > 0 && (
              <Indicator icon={<GitFork size={11} />} value={stats.forks} tone="primary" />
            )}
            <StanceMeter stats={stats} />
          </div>
        )}
      </div>

      <Handle
        type="source"
        position={Position.Right}
        className="!w-2 !h-2 !bg-[var(--color-border-bright)] !border-[var(--color-surface)]"
      />
    </div>
  );
}

function Indicator({ icon, value, tone }: { icon: ReactNode; value: number; tone: string }) {
  const color =
    tone === "evidence" ? "text-[var(--color-evidence)]"
    : tone === "primary" ? "text-[var(--color-primary)]"
    : "text-[var(--color-ink-faint)]";
  return (
    <span className={cn("inline-flex items-center gap-0.5 text-[10px] font-mono", color)}>
      {icon}{value}
    </span>
  );
}

// honest stance meter: renders the real agree/disagree/unsure split when the
// data has one, a single agree segment when only that was recorded, and
// nothing at all when there is no stance data.
function StanceMeter({ stats }: { stats: NonNullable<GraphNode["stats"]> }) {
  const { agree, disagree, unsure } = stats;
  if (typeof agree !== "number") return null;

  if (typeof disagree !== "number") {
    return (
      <span className="ml-auto flex items-center gap-1" title={`${agree}% agree (no full split recorded)`}>
        <Scale size={11} className="text-[var(--color-ink-faint)]" />
        <span className="flex h-1.5 w-8 overflow-hidden rounded-full bg-[var(--color-surface-3)]">
          <span className="bg-[var(--color-support)]" style={{ width: `${agree}%` }} />
        </span>
        <span className="font-mono text-[9px] text-[var(--color-ink-faint)]">{agree}%</span>
      </span>
    );
  }

  const unsureShare = typeof unsure === "number" ? unsure : Math.max(0, 100 - agree - disagree);
  return (
    <span
      className="ml-auto flex items-center gap-1"
      title={`${agree}% agree · ${disagree}% disagree · ${unsureShare}% unsure`}
    >
      <Scale size={11} className="text-[var(--color-ink-faint)]" />
      <span className="flex h-1.5 w-8 overflow-hidden rounded-full bg-[var(--color-surface-3)]">
        <span className="bg-[var(--color-support)]" style={{ width: `${agree}%` }} />
        <span className="bg-[var(--color-ink-faint)]" style={{ width: `${unsureShare}%` }} />
        <span className="bg-[var(--color-rebut)]" style={{ width: `${disagree}%` }} />
      </span>
    </span>
  );
}

function RailButton({ disabled, onClick, icon, label }: { disabled: boolean; onClick: () => void; icon: ReactNode; label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={(event) => { event.stopPropagation(); onClick(); }}
      className={cn(
        "grid h-5 w-5 shrink-0 place-items-center rounded border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-ink-muted)] transition-colors hover:text-[var(--color-ink)]",
        disabled && "pointer-events-none opacity-30",
      )}
    >
      {icon}
    </button>
  );
}

function TrayChip({ icon, color, label }: { icon: ReactNode; color: string; label: string }) {
  return (
    <span
      className="inline-flex h-5 items-center gap-1 rounded-full border px-1.5 font-mono text-[9px] uppercase tracking-wider"
      style={{
        color,
        borderColor: `color-mix(in srgb, ${color} 38%, var(--color-border) 62%)`,
        background: `color-mix(in srgb, ${color} 8%, var(--color-surface) 92%)`,
      }}
    >
      {icon}{label}
    </span>
  );
}

function DiffText({ from, to }: { from: string; to: string }) {
  const tokens = useMemo(() => wordDiff(from, to), [from, to]);
  return (
    <span>
      {tokens.map((token, index) => (
        <span
          key={index}
          className={cn(
            token.kind === "added" && "rounded-sm bg-[color-mix(in_srgb,var(--color-support)_22%,transparent)] text-[var(--color-support)]",
            token.kind === "removed" && "rounded-sm bg-[color-mix(in_srgb,var(--color-rebut)_16%,transparent)] text-[var(--color-rebut)] line-through opacity-70",
          )}
        >
          {token.text}
          {index < tokens.length - 1 ? " " : ""}
        </span>
      ))}
    </span>
  );
}

export const StrainNode = memo(StrainNodeComponent);
